/**
 * ThreatTraceabilityTable — Step 4 row adapter + bare DataTable.
 *
 * Fetches the analysis-results blob via the canonical
 * `getAnalysisResults(threadId)`, builds one row per Threat via
 * `toChainRows`, and renders 5 columns through the shared DataTable.
 *
 * Step 4 ships the 5-column scaffold with default cell rendering
 * (Threat ID, STRIDE pill, Asset pill, Chain placeholder, expand
 * placeholder). Step 5 swaps the Chain column's placeholder for the
 * real 7-link ChainStrip; Step 6 wires the expand-row pane; Step 7
 * adds the toolbar + filters.
 *
 * All colour + label decisions for entity types route through the
 * Step-1 centralised utilities in `utils/severityStyle.js`.
 */
import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/ui/DataTable/DataTable';
import TableToolbar from '../../../components/ui/DataTable/TableToolbar';
import FilterDrawer from '../../../components/ui/DataTable/FilterDrawer';
import dtStyles from '../../../components/ui/DataTable/DataTable.module.css';
import CategoryPill from '../../../components/ui/CategoryPill';
import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';
import {
  getStrideLabel,
  STRIDE_CATEGORIES,
  TREATMENT_DECISIONS,
} from '../../../utils/severityStyle';
import { AnalysisEmpty } from '../../../components/ui/Empty';
import { getAnalysisResults } from '../api';
import { toChainRows } from './toChainRows';
import ChainStrip from './ChainStrip';
import ThreatDetailPane from './ThreatDetailPane';
import styles from './ThreatTraceabilityTable.module.css';

const ThreatTraceabilityTable = ({ threadId, projectLoading = false }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Local expansion state — Set of row ids currently expanded.
  // Multiple rows can be open simultaneously (matches legacy).
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // Canonical filter state — both multi-select. Empty array =
  // pass-through for that section.
  const [filterOpen, setFilterOpen] = useState(false);
  const [strideFilters, setStrideFilters] = useState([]);
  const [treatmentFilters, setTreatmentFilters] = useState([]);

  const toggleRow = (id) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    // No thread yet (analysis not started) — clear the initial loading
    // flag so the no-analysis empty state renders instead of skeleton
    // rows forever. The wrapper gates the project-document fetch window
    // via `projectLoading`, so reaching here with no threadId means the
    // project resolved with no workflow.
    if (!threadId) {
      setResults(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAnalysisResults(threadId);
        if (!cancelled) setResults(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching analysis results:', err);
        setError(err.message || 'Failed to load analysis results');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchResults();
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  const rows = useMemo(() => (results ? toChainRows(results) : []), [results]);

  // Filtered rows — STRIDE + Treatment compose with AND. Empty
  // filter array on a section = pass-through. Treatment is
  // threat-level: a row matches if ANY of its risks has a
  // treatment_decision in the selected set.
  const filteredRows = useMemo(() => {
    if (strideFilters.length === 0 && treatmentFilters.length === 0) return rows;
    return rows.filter((row) => {
      if (strideFilters.length > 0 && !strideFilters.includes(row.stride_category)) {
        return false;
      }
      if (treatmentFilters.length > 0) {
        const has = row.risks.some((r) => treatmentFilters.includes(r.treatment_decision));
        if (!has) return false;
      }
      return true;
    });
  }, [rows, strideFilters, treatmentFilters]);

  const filters = useMemo(
    () => [
      {
        key: 'stride',
        label: 'STRIDE',
        values: strideFilters,
        onChange: setStrideFilters,
        options: STRIDE_CATEGORIES.map((c) => ({
          value: c,
          label: getStrideLabel(c),
          count: rows.filter((r) => r.stride_category === c).length,
        })),
      },
      {
        key: 'treatment',
        label: 'Treatment',
        values: treatmentFilters,
        onChange: setTreatmentFilters,
        options: TREATMENT_DECISIONS.map((d) => ({
          value: d,
          label: d,
          count: rows.filter((r) => r.risks.some((rk) => rk.treatment_decision === d)).length,
        })),
      },
    ],
    [rows, strideFilters, treatmentFilters],
  );

  const hasActiveFilters = strideFilters.length > 0 || treatmentFilters.length > 0;

  const columns = useMemo(
    () => [
      {
        key: 'threat_id',
        label: 'Threat',
        width: 180,
        render: (row) => <span className={styles.plainText}>{row.threat_id}</span>,
      },
      {
        key: 'asset',
        label: 'Asset',
        width: 180,
        render: (row) => (
          <span className={styles.plainText} title={row.asset?.asset_id}>
            {row.asset?.asset_name || row.asset?.asset_id || ''}
          </span>
        ),
      },
      {
        key: 'stride_category',
        label: 'STRIDE',
        width: 180,
        render: (row) =>
          row.stride_category ? (
            <CategoryPill label={getStrideLabel(row.stride_category)} size="sm" />
          ) : null,
      },
      {
        key: '_chain',
        label: 'Chain',
        render: (row) => (
          <div className={styles.chainCell}>
            <ChainStrip row={row} />
            {expandedRows.has(row.id) && <ThreatDetailPane row={row} />}
          </div>
        ),
      },
      {
        key: '_actions',
        label: '',
        width: 56,
        align: 'right',
        render: (row) => {
          const isExpanded = expandedRows.has(row.id);
          return (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={isExpanded ? styles.expandOpen : ''}
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(row.id);
              }}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              aria-expanded={isExpanded}
            >
              <Icon name="chevron-down" size={14} className={styles.expandChevron} />
            </Button>
          );
        },
      },
    ],
    [expandedRows],
  );

  if (error) {
    return <div className={styles.statusError}>Error: {error}</div>;
  }

  // Analysis not started — no workflow to trace. (Guarded by
  // projectLoading so we don't flash this during the getSession fetch.)
  if (!projectLoading && !threadId) {
    return <AnalysisEmpty />;
  }

  // Analysis started but produced no threats — unified data-empty state.
  // A filtered-to-zero result keeps the table (its own emptyState) so the
  // filter drawer stays reachable.
  if (!projectLoading && !loading && rows.length === 0 && !hasActiveFilters) {
    return (
      <AnalysisEmpty
        icon="network"
        title="No threats to trace yet"
        description="Complete the TARA workflow to build the threat-traceability chain."
      />
    );
  }

  const toolbar = (
    <TableToolbar
      onFilterToggle={() => setFilterOpen((o) => !o)}
      filterButtonActive={filterOpen || hasActiveFilters}
      count={{ value: filteredRows.length, label: 'threats' }}
    />
  );

  return (
    <div className={`${dtStyles.shell} ${dtStyles.shellPlain}`}>
      <FilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
      />
      <DataTable
        data={filteredRows}
        columns={columns}
        rowKey="id"
        loading={loading || projectLoading}
        toolbar={toolbar}
        onRowClick={(row) => toggleRow(row.id)}
        emptyState={{
          title: 'No matching threats',
          description: 'No threats match the selected STRIDE / Treatment filters.',
        }}
      />
    </div>
  );
};

export default ThreatTraceabilityTable;
